import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../../utils/site";

const AddBlog = () => {
  const navigate = useNavigate();
  const [author, setAuthor] = useState("");
  const [blog, setBlog] = useState({
    title: "",
    content: "",
    category: "Other",
    image_url: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageSource, setImageSource] = useState("url");
  const [excerpt, setExcerpt] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("published");

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.name || storedUser?.email || storedUser?.id) {
      setAuthor(storedUser.name || storedUser.email || String(storedUser.id));
    }
  }, []);

  const handleChange = (e) => {
    setBlog({ ...blog, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleImageUrlChange = (e) => {
    setBlog({ ...blog, image_url: e.target.value });
    setImagePreview(e.target.value ? e.target.value : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!author) {
      toast.error("Author missing.");
      setLoading(false);
      return;
    }

    if (imageSource === "url" && !blog.image_url.trim()) {
      setError("Please provide an image URL or choose upload image.");
      setLoading(false);
      return;
    }
    if (imageSource === "upload" && !imageFile) {
      setError("Please upload an image file or choose image URL.");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (imageSource === "upload" && imageFile) {
        const formData = new FormData();
        formData.append("title", blog.title);
        formData.append("content", blog.content);
        formData.append("author", author);
        formData.append("category", blog.category || "Other");
        formData.append("excerpt", excerpt || "");
        formData.append("status", status);
        formData.append("image", imageFile);

        await axios.post(getApiUrl("/api/blogs"), formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(
          getApiUrl("/api/blogs"),
          {
            title: blog.title,
            content: blog.content,
            author,
            category: blog.category || "Other",
            image_url: blog.image_url,
            excerpt: excerpt || "",
            status,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success("Blog added successfully.");
      navigate("/admin/blogs");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Failed to add blog.");
      toast.error(requestError.response?.data?.error || "Failed to add blog.");
    } finally {
      setLoading(false);
    }
  };

  const categories = ["Gold", "Silver", "Gemstones", "Design Trends", "Care & Maintenance", "Events", "Other"];

  return (
    <AdminPageShell
      eyebrow="Editorial publishing"
      title="Add Blog"
      description="Create a polished editorial post with image support, excerpt control, and publishing status."
    >
      <AdminSurface title="Blog composer" subtitle="Build a new article for the Roha editorial section">
        <form className="roha-admin-form-grid" onSubmit={handleSubmit}>
          <div className="roha-admin-form-grid roha-admin-form-grid--two">
            <div className="roha-admin-field">
              <label>Title</label>
              <input type="text" name="title" className="form-control" value={blog.title} onChange={handleChange} required />
            </div>
            <div className="roha-admin-field">
              <label>Category</label>
              <select name="category" className="form-select" value={blog.category} onChange={handleChange}>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="roha-admin-form-grid roha-admin-form-grid--two">
            <div className="roha-admin-field">
              <label>Excerpt</label>
              <input className="form-control" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} />
            </div>
            <div className="roha-admin-field">
              <label>Status</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="roha-admin-field">
            <label>Content</label>
            <textarea
              name="content"
              className="form-control"
              rows="8"
              value={blog.content}
              onChange={handleChange}
              required
            />
          </div>

          <div className="roha-admin-toolbar">
            <label className="roha-admin-pill">
              <input
                type="radio"
                checked={imageSource === "url"}
                onChange={() => {
                  setImageSource("url");
                  setImageFile(null);
                  setImagePreview(blog.image_url);
                }}
              />
              Image URL
            </label>
            <label className="roha-admin-pill">
              <input
                type="radio"
                checked={imageSource === "upload"}
                onChange={() => {
                  setImageSource("upload");
                  setBlog({ ...blog, image_url: "" });
                  setImagePreview(null);
                }}
              />
              Upload image
            </label>
          </div>

          {imageSource === "url" ? (
            <div className="roha-admin-field">
              <label>Image URL</label>
              <input
                type="text"
                className="form-control"
                value={blog.image_url}
                onChange={handleImageUrlChange}
                placeholder="https://..."
              />
            </div>
          ) : (
            <div className="roha-admin-field">
              <label>Upload image</label>
              <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
            </div>
          )}

          {imagePreview ? (
            <img
              src={getMediaUrl(imagePreview)}
              alt="Preview"
              className="roha-admin-image-thumb"
              style={{ width: 180, height: 180 }}
            />
          ) : null}

          {error ? <p className="roha-admin-muted">{error}</p> : null}

          <div className="roha-admin-toolbar">
            <button type="submit" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary" disabled={loading}>
              {loading ? "Adding..." : "Publish blog"}
            </button>
          </div>
        </form>
      </AdminSurface>
    </AdminPageShell>
  );
};

export default AddBlog;
